// Origin-checked iframe resize handshake — shared, side-effect-free module so
// both the entry (main.js) and the ported app.vue (P3) can import it without a
// main ↔ app.vue circular import or dragging the entry's bootstrap side-effect
// into app.vue's dependency graph. Mirrors app.vue's parentOrigin + informResize
// (the frozen contract the embedder's classes/widget.js receiver parses).

// Resolve the host-page origin from an ALREADY-EXTRACTED `src` candidate plus the
// referrer fallback. Returns scheme+host(+non-default port) only — never a path,
// never a query string.
//
// 🔑 THIS IS THE ONLY CANDIDATE ORDERING AND THE ONLY PARSE. app.vue's
// `parentOrigin` computed and `resolveParentOrigin` below both funnel through it.
// Independent review MEASURED the previous arrangement — two structurally similar
// resolvers reading two different inputs — producing DIFFERENT origins for the same
// embed, while a comment claimed they "can never disagree". Two implementations of
// one question is how that happens; there is now one.
export function originFromCandidates(src, referrer) {
  const candidates = [];
  if (src) candidates.push(src);
  if (referrer) candidates.push(referrer);
  for (const candidate of candidates) {
    try {
      const origin = new URL(candidate).origin;
      // 🪤 `URL.origin` is the STRING 'null' for an opaque origin — a `data:`,
      // `blob:` or `about:` candidate, and any scheme the browser does not treat
      // as tuple-origined. It is truthy, so a plain `if (origin)` stamps the
      // four characters `null` as though they were a site, and postMessage would
      // reject that target anyway. Unresolved is unresolved: fall through.
      if (origin && origin !== 'null') return origin;
    } catch (e) {
      /* not a parseable absolute URL — try the next candidate */
    }
  }
  return '';
}

// Resolve the host-page origin used as the postMessage targetOrigin — NEVER
// '*'. Prefer the `src=<encoded host URL>` param the embedder appends to the
// iframe query (classes/widget.js), then fall back to document.referrer; return
// '' if neither parses (informResize then drops the message rather than
// broadcasting to '*').
export function resolveParentOrigin(search, referrer) {
  let src = '';
  try {
    // 🪤 LAST-WINS, and `get()` — which is first-wins — was a live hazard here, not only a
    // telemetry one. `classes/widget.js` copies the host page's whole query string into
    // the iframe URL before appending its own `src=<encoded host href>`, so a page (or a
    // crafted link) carrying `?src=https://attacker.example` chose the origin this
    // handshake posts to. The loader's append is unconditional and last, so the final
    // value is always the one we put there. Same read as core/faro.js's redaction.
    src = new URLSearchParams(search || '').getAll('src').pop() || '';
  } catch (e) {
    /* malformed search string — fall through to referrer */
  }
  return originFromCandidates(src, referrer);
}

// Post the origin-checked resize handshake the host embedder listens for
// (classes/widget.js parses `{id,type:'resize',height,width}`, JSON-encoded, and
// only accepts it from the matching origin). Drops the message when origin is ''
// — never targets '*'.
export function informResize(el, id, origin) {
  if (!origin || !el) return;
  const msg = JSON.stringify({
    id,
    type: 'resize',
    width: el.scrollWidth,
    height: el.scrollHeight,
  });
  window.parent.postMessage(msg, origin);
}
